import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { ITEM_ICON } from "@/lib/gameData";
import { Search } from "lucide-react";

export function MuseumPanel() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(false);

  useEffect(() => {
    api.get("/api/game/museum").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  const grouped = useMemo(() => {
    if (!data) return {};
    const q = filter.toLowerCase();
    const g = {};
    for (const e of data.entries) {
      if (q && !e.name.toLowerCase().includes(q)) continue;
      if (onlyMissing && e.obtained) continue;
      const key = e.cat || "misc";
      (g[key] ||= []).push(e);
    }
    return g;
  }, [data, filter, onlyMissing]);

  if (!data) return <div className="p-6 text-stone-500 italic">Loading museum…</div>;

  const pct = Math.round((data.obtained / data.total) * 100);
  return (
    <div className="p-4 lg:p-6 space-y-6" data-testid="museum-panel">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h2 className="font-display text-3xl heading-gilded mb-1">Museum</h2>
          <p className="text-stone-500 font-body">
            Your realm-wide collection log. Every unique item you have ever held.
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-numeric text-amber-300" data-testid="museum-count">{data.obtained}/{data.total}</div>
          <div className="text-[10px] uppercase tracking-widest font-numeric text-stone-500">{pct}% catalogued</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex items-center gap-2 flex-1 max-w-md">
          <Search size={14} className="text-stone-500" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search items…"
            className="flex-1 bg-stone-950 border border-stone-700 rounded px-2 py-1 text-sm font-body focus:border-amber-600 outline-none"
            data-testid="museum-search"
          />
        </div>
        <label className="flex items-center gap-2 text-xs font-body text-stone-400">
          <input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} data-testid="museum-missing-toggle" />
          Show only missing
        </label>
      </div>

      <div className="space-y-6">
        {Object.keys(grouped).sort().map((cat) => (
          <section key={cat}>
            <div className="flex items-center gap-4 mb-2">
              <h3 className="font-display text-lg heading-gilded uppercase tracking-widest">{cat}</h3>
              <div className="flex-1 divider-rune" />
            </div>
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
              {grouped[cat].map((e) => (
                <div
                  key={e.id}
                  title={`${e.name}${e.obtained ? "" : " (not yet obtained)"}`}
                  data-testid={`museum-item-${e.id}`}
                  className={
                    "aspect-square panel-inset flex items-center justify-center relative " +
                    (e.obtained ? "" : "opacity-30 grayscale")
                  }
                >
                  <span className="text-3xl">{ITEM_ICON[e.id] || "◇"}</span>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
